import { useCallback, useEffect, useState } from 'react';
import { ConsoleApiError } from '../services/console';

export function messageFor(error: unknown, fallback: string): string {
  return error instanceof ConsoleApiError ? error.message : fallback;
}

/** Keep the supplied decimal intact; converting to a number loses precision. */
export function usd(value: string | null | undefined): string {
  return value == null ? 'Unavailable' : `${value} USD`;
}

export function utcTime(seconds: number): string {
  const date = new Date(seconds * 1000);
  if (!Number.isFinite(date.getTime()) || seconds <= 0) return 'Unavailable';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
    timeZone: 'UTC', hour12: false,
  }).format(date);
}

export function useReadResource<T>(load: () => Promise<T>, fallback: string) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const retry = useCallback(() => setRevision(value => value + 1), []);

  useEffect(() => {
    let current = true;
    setLoading(true);
    setError('');
    setData(null);
    void load().then(result => {
      if (current) setData(result);
    }).catch(cause => {
      if (current) setError(messageFor(cause, fallback));
    }).finally(() => {
      if (current) setLoading(false);
    });
    return () => { current = false; };
  }, [load, fallback, revision]);

  return { data, loading, error, retry };
}
